<script>
  import { onDestroy, onMount, tick } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { fly } from "svelte/transition";
  import Header from "../../../../lib/components/Header.svelte";
  import ButtonNew from "../../../../lib/components/ButtonNew.svelte";
  import { Loading } from "carbon-components-svelte";
  import { useUrlParams } from "@stores/urlStore.js";
  import { fetchWithSession } from "@utils/fetchWithSession.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import NumberRuleModal from "../components/NumberRuleModal.svelte";
  import { TextInput } from "carbon-components-svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import ContentSwitcher from "../../../../lib/components/ContentSwitcher.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import EditIcon from "../../settings/svg/EditIcon.svelte";
  import RemoveIcon from "../../settings/svg/RemoveIcon.svelte";
  import { InfoBlock, SectionWrapper } from "paxray-components-svelte";
  import ChatbotIcon from "@components/common/icons/app/ChatbotIcon.svelte";
  import Modal from "@components/Modal.svelte";
  import CaseIdGraphPreview from "./CaseIdGraphPreview.svelte";

  const urlParams = useUrlParams();
  const step = PROJECT_STAGES.TASK_REGISTRATION.steps.NUMBER_ASSIST;
  const stage = PROJECT_STAGES.TASK_REGISTRATION.id;

  $: ({ idproject } = $urlParams);

  let headers = [
    {
      key: "record_type",
      value: "Object Type",
      tooltip:
        "Entries with the same object type name are grouped together for data collection and analysis.",
    },
    { key: "prefix", value: "Number Range" },
    { key: "length", value: "Length" },
    { key: "padding", value: "Padding" },
    { key: "track", value: "Track" },
    { key: "menu", value: "Actions", width: "105px" },
  ];
  let rows = [];
  let addRuleModalOpen = false;
  let openDangerModal = false;
  let idToDelete = null;
  let currentRule = {};
  let searchValue = "";
  let suggestionSearchValue = "";
  let minConnections = 0;
  let loadedProjectId = null;
  let configuredRangesLoading = false;
  let suggestionRows = [];
  let suggestionsLoading = false;
  let configuredRows = [];
  let prefillNumbersToCheck = "";
  let prefillRequestId = 0;
  let configuredPage = 1;
  let configuredPageSize = 10;
  let suggestionPage = 1;
  let suggestionPageSize = 10;
  let lastSearchValue = "";
  let lastSuggestionSearchValue = "";
  // Sorting is handled externally (see suggestionRowsWithState) so that
  // suggestions with an existing rule always stay at the end of the table.
  // The no-op comparators keep the header sort UI while preserving row order.
  let suggestionSortKey = null;
  let suggestionSortDirection = "none";
  let suggestionView = 0;
  const suggestionBaseHeaders = [
    { key: "prefix", value: "Prefix", sort: () => 0 },
    { key: "length", value: "Length", sort: () => 0 },
    { key: "count", value: "Count", sort: () => 0 },
    { key: "sample_values_display", value: "Examples", sort: () => 0 },
    { key: "sample_clearnames_display", value: "Top Apps", sort: () => 0 },
    { key: "windowtitles_display", value: "Masks", sort: () => 0 },
    { key: "add", value: "Actions", width: "112px", sort: false },
  ];
  const suggestionDetailHeaders = [
    { key: "prefix", value: "Prefix", sort: () => 0 },
    { key: "length", value: "Length", sort: () => 0 },
    { key: "count", value: "Count", sort: () => 0 },
    { key: "sample_values_display", value: "Examples", sort: () => 0 },
    { key: "tasks_display", value: "Tasks", sort: () => 0 },
    { key: "activities_display", value: "Activities", sort: () => 0 },
    { key: "connections_count", value: "Connections", sort: () => 0 },
    { key: "sample_clearnames_display", value: "Top Apps", sort: () => 0 },
    { key: "windowtitles_display", value: "Masks", sort: () => 0 },
    { key: "add", value: "Actions", width: "112px", sort: false },
  ];
  $: suggestionHeaders =
    suggestionView === 0 ? suggestionBaseHeaders : suggestionDetailHeaders;
  let paxbotVisible = false;
  let paxbotSelectedRow = null;
  let paxbotInput = "";
  let paxbotLoading = false;
  let paxbotTyping = false;
  let paxbotTypingTimer;
  let paxbotMessagesElement;
  let paxbotScrollSignature = "";
  let paxbotChatMessages = [];
  let numberRangePreviewOpen = false;
  let rangeTourMounted = false;
  let unregisterRangeTour;
  let showRangeGuide = false;
  let rangeGuideStep = 0;
  let rangeGuideSteps = [];

  onMount(() => {
    rangeTourMounted = true;
    return () => {
      rangeTourMounted = false;
      unregisterRangeTour?.();
    };
  });

  $: if (rangeTourMounted && !pageLoading) {
    if (!unregisterRangeTour)
      unregisterRangeTour = registerViewTour(startRangeGuide);
  } else {
    unregisterRangeTour?.();
    unregisterRangeTour = null;
    showRangeGuide = false;
  }

  async function startRangeGuide() {
    if (addRuleModalOpen || openDangerModal || numberRangePreviewOpen) return;
    const completed = await projectListStore.isStepCompleted(
      idproject,
      step,
      stage,
    );
    if (!rangeTourMounted || pageLoading) return;
    paxbotVisible = false;
    rangeGuideSteps = [
      {
        target: '[data-guide="number-range-overview"]',
        title: "Identify business objects by their numbers",
        text: "Define number ranges to recognize business objects, such as orders, tickets or invoices, in registered tasks. These rules help connect work involving the same object during analysis.",
      },
      {
        target: '[data-guide="configured-number-ranges"]',
        title: "Review configured number ranges",
        text: "The upper table shows your existing rules. Review the object type, number range prefix and length. Use “Edit” to change a rule or “Add Number Range” to create one manually. Rules with the same Object Type name are grouped together for data collection and analysis.",
      },
      ...(filteredSuggestionRows.length > 0
        ? [
            {
              target: '[data-guide="suggested-number-ranges"] table',
              title: "Explore suggested number ranges",
              text: "The lower table shows patterns found in your project data. Review the examples, counts and applications to decide whether a suggestion represents a relevant business object. Suggestions are candidates, not configured rules.",
            },
            {
              target: '[data-guide="suggested-number-ranges"]',
              highlightSelector: ".suggestion-table-controls, .bx--search",
              title: "Inspect the context",
              text: "Switch to “Details” to see related tasks, activities and connections. Use search to find specific candidates, or increase “Min. connections” to focus on patterns with more connections. “Ask Paxbot” provides help for the selected suggestion.",
            },
            {
              target:
                '[data-guide="suggested-number-ranges"] button[aria-label="Add as rule"]:not(:disabled)',
              title: "Define and test a rule",
              text: "Choose “Add as rule” on a relevant suggestion. Give it a meaningful Object Type, check the prefix and length, and use “Test Numbers” to try example values. Enable “Ignore leading zeros” if needed and “Track matching objects” to use matching objects in analysis. Click “Save” to add the rule.",
            },
          ]
        : []),
      {
        target:
          '[data-guide="complete-number-ranges"] > button, [data-guide="complete-number-ranges"] > .completed-badge',
        title: completed
          ? "Number range setup is complete"
          : "Complete number range setup",
        text: completed
          ? "This project step is complete. You can still review, change or extend your number range rules here and later during analysis. Return to Project Progress to continue with your project."
          : "Review your configured rules, then click “Complete Number Range Setup” and confirm. If no number ranges are needed for your project, you can complete this step without adding a rule. You can still change or extend the rules later during analysis.",
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    rangeGuideStep = 0;
    showRangeGuide = rangeGuideSteps.length > 0;
  }
  let previewRow = null;
  let previewGraph = { nodes: [], edges: [] };
  let previewLoading = false;
  let previewError = "";
  let previewRequestId = 0;

  async function scrollPaxbotToBottom() {
    await tick();
    if (!paxbotMessagesElement) return;
    paxbotMessagesElement.scrollTop = paxbotMessagesElement.scrollHeight;
  }

  function clearPaxbotTyping() {
    if (paxbotTypingTimer) {
      clearTimeout(paxbotTypingTimer);
      paxbotTypingTimer = null;
    }
    paxbotTyping = false;
  }

  function animatePaxbotAnswer(answer) {
    clearPaxbotTyping();
    const fullText = String(answer || "").trim();
    const typingId = `paxbot-${Date.now()}-${Math.random()}`;
    let visibleLength = 0;
    paxbotTyping = true;
    paxbotChatMessages = [
      ...paxbotChatMessages,
      { role: "assistant", text: "", typingId },
    ];

    const typeNext = () => {
      visibleLength = Math.min(
        visibleLength + (fullText.length > 180 ? 3 : 2),
        fullText.length,
      );
      paxbotChatMessages = paxbotChatMessages.map((message) =>
        message.typingId === typingId
          ? { ...message, text: fullText.slice(0, visibleLength) }
          : message,
      );
      if (visibleLength < fullText.length) {
        paxbotTypingTimer = setTimeout(typeNext, 14);
      } else {
        paxbotTypingTimer = null;
        paxbotTyping = false;
      }
    };

    typeNext();
  }

  $: {
    const lastPaxbotMessage = paxbotChatMessages[paxbotChatMessages.length - 1];
    const nextPaxbotScrollSignature = [
      paxbotVisible,
      paxbotLoading,
      paxbotChatMessages.length,
      lastPaxbotMessage?.role || "",
      lastPaxbotMessage?.text || "",
    ].join("|");
    if (paxbotVisible && nextPaxbotScrollSignature !== paxbotScrollSignature) {
      paxbotScrollSignature = nextPaxbotScrollSignature;
      scrollPaxbotToBottom();
    }
  }

  $: filteredRows = searchValue
    ? configuredRows.filter((row) =>
        ["record_type", "prefix"].some((key) => {
          const val = row[key];
          return (
            val != null &&
            String(val).toLowerCase().includes(searchValue.toLowerCase())
          );
        }),
      )
    : configuredRows;

  function valueMatchesRule(value, rule) {
    let inputStr = String(value);
    const prefixStr = String(rule.prefix ?? "");

    if (rule.padding) {
      inputStr = inputStr.replace(/^0+/, "") || "0";
    }

    return (
      inputStr.length === Number(rule.length) && inputStr.startsWith(prefixStr)
    );
  }

  function findSuggestionForRule(rule) {
    return suggestionRows.find((suggestion) => {
      if (
        String(suggestion.prefix) === String(rule.prefix) &&
        Number(suggestion.length) === Number(rule.length)
      ) {
        return true;
      }
      const sampleValues = Array.isArray(suggestion.sample_values)
        ? suggestion.sample_values
        : [];
      return (
        sampleValues.length > 0 &&
        sampleValues.some((value) => valueMatchesRule(value, rule))
      );
    });
  }

  $: configuredRows = rows.map((row) => {
    const suggestion = findSuggestionForRule(row);
    const connections = suggestion?.connections || row.connections || [];
    return {
      ...row,
      connections,
      connections_display: formatConnections(connections),
      hasPreviewConnections:
        Array.isArray(connections) && connections.length > 0,
    };
  });

  $: suggestionRowsWithState = suggestionRows
    .map((suggestion) => {
      const sampleValues = Array.isArray(suggestion.sample_values)
        ? suggestion.sample_values
        : [];

      const hasExistingRule =
        sampleValues.length > 0 &&
        rows.some((rule) =>
          sampleValues.every((value) => valueMatchesRule(value, rule)),
        );

      return {
        ...suggestion,
        hasExistingRule,
      };
    })
    .sort((a, b) => {
      if (a.hasExistingRule !== b.hasExistingRule) {
        return a.hasExistingRule ? 1 : -1;
      }
      if (suggestionSortKey && suggestionSortDirection !== "none") {
        const direction = suggestionSortDirection === "descending" ? -1 : 1;
        const aVal = a[suggestionSortKey];
        const bVal = b[suggestionSortKey];
        const cmp =
          typeof aVal === "number" && typeof bVal === "number"
            ? aVal - bVal
            : String(aVal ?? "").localeCompare(String(bVal ?? ""));
        if (cmp !== 0) return direction * cmp;
      }
      return b.count - a.count;
    });

  $: availableSuggestionRows = suggestionRowsWithState.filter(
    (row) => !row.hasExistingRule,
  );

  $: connectionFilteredSuggestionRows =
    Number(minConnections) > 0
      ? availableSuggestionRows.filter(
          (row) =>
            (row.connections || []).reduce(
              (total, connection) => total + Number(connection.count || 0),
              0,
            ) >= Number(minConnections),
        )
      : availableSuggestionRows;

  $: filteredSuggestionRows = suggestionSearchValue
    ? connectionFilteredSuggestionRows.filter((row) =>
        suggestionHeaders.some((h) => {
          if (h.key === "add") return false;
          const val = row[h.key];
          return (
            val != null &&
            String(val)
              .toLowerCase()
              .includes(suggestionSearchValue.toLowerCase())
          );
        }),
      )
    : connectionFilteredSuggestionRows;

  $: configuredPageCount = Math.max(
    Math.ceil(filteredRows.length / configuredPageSize),
    1,
  );
  $: suggestionPageCount = Math.max(
    Math.ceil(filteredSuggestionRows.length / suggestionPageSize),
    1,
  );
  $: if (configuredPage > configuredPageCount) {
    configuredPage = configuredPageCount;
  }
  $: if (suggestionPage > suggestionPageCount) {
    suggestionPage = suggestionPageCount;
  }
  $: if (searchValue !== lastSearchValue) {
    lastSearchValue = searchValue;
    configuredPage = 1;
  }
  $: if (suggestionSearchValue !== lastSuggestionSearchValue) {
    lastSuggestionSearchValue = suggestionSearchValue;
    suggestionPage = 1;
  }

  async function getData() {
    configuredRangesLoading = true;
    const params = new URLSearchParams({ project_id: String(idproject) });
    try {
      const response = await fetchWithSession(
        `/api/damprojects/number-ranges?${params.toString()}`,
        { method: "GET" },
      );
      const data = await response.json();
      rows = data.sort((a, b) => {
        if (a.record_type.toLowerCase() < b.record_type.toLowerCase())
          return -1;
        if (a.record_type.toLowerCase() > b.record_type.toLowerCase()) return 1;
        return 0;
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      configuredRangesLoading = false;
    }
  }

  async function getNumberRangeSuggestions() {
    suggestionsLoading = true;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/numberRangeSuggestions/${idproject}`,
        { method: "GET" },
      );
      const data = await response.json();
      const rawSuggestions = Array.isArray(data?.numberRanges)
        ? data.numberRanges
        : [];

      suggestionRows = rawSuggestions
        .map((suggestion, index) => {
          const sampleValues = Array.isArray(suggestion.sample_values)
            ? suggestion.sample_values
            : [];
          const sampleClearnames = Array.isArray(suggestion.sample_clearnames)
            ? suggestion.sample_clearnames
            : [];
          const locations = Array.isArray(suggestion.locations)
            ? suggestion.locations
            : [];
          const windowtitles = Array.isArray(suggestion.windowtitles)
            ? suggestion.windowtitles
            : [];
          const tasks = Array.isArray(suggestion.tasks) ? suggestion.tasks : [];
          const activities = Array.isArray(suggestion.activities)
            ? suggestion.activities
            : [];
          const connections = Array.isArray(suggestion.connections)
            ? suggestion.connections
            : [];
          return {
            id: `number-range-suggestion-${index}-${String(suggestion.prefix ?? "")}-${String(suggestion.length ?? "")}`,
            prefix: String(suggestion.prefix ?? ""),
            length: Number(suggestion.length ?? 0),
            count: Number(suggestion.count ?? 0),
            sample_values: sampleValues,
            sample_values_display: sampleValues.join(", "),
            sample_clearnames: sampleClearnames,
            sample_clearnames_display: sampleClearnames.join(", "),
            tasks,
            tasks_display: formatTopValues(tasks),
            activities,
            activities_display: formatTopValues(activities),
            connections,
            connections_count: connections.reduce(
              (total, connection) => total + Number(connection.count || 0),
              0,
            ),
            connections_display: formatConnections(connections),
            windowtitles,
            windowtitles_display: formatTopValues(windowtitles),
            locations,
            locations_display: formatTopValues(locations),
          };
        })
        .sort((a, b) => b.count - a.count);
    } catch (error) {
      suggestionRows = [];
      console.error("Error fetching number range suggestions:", error);
    } finally {
      suggestionsLoading = false;
    }
  }

  function formatTopValues(values) {
    return (
      values
        .slice(0, 2)
        .map(
          (value) =>
            `${truncateTopValue(displayAnonymizedText(value.name))} (${value.count}x)`,
        )
        .join(", ") + (values.length > 2 ? "..." : "")
    );
  }

  function displayAnonymizedText(value) {
    return String(value ?? "")
      .replace(/<<<space>>>/g, " ")
      .replace(/<<<([^>]+)>>>/g, "$1");
  }

  function truncateTopValue(value) {
    const text = String(value ?? "");
    return text.length > 15 ? `${text.slice(0, 15)}...` : text;
  }

  function getTopValuesTooltip(values) {
    return values
      .slice(0, 10)
      .map((value) => `${displayAnonymizedText(value.name)} (${value.count}x)`)
      .join("\n");
  }

  function formatConnections(connections, limit = 2) {
    return (
      connections
        .slice(0, limit)
        .map(
          (connection) =>
            `${connection.path.join(" - ")} (${connection.count}x)`,
        )
        .join(", ") + (connections.length > limit ? "..." : "")
    );
  }

  function getPaxbotTopValues(values) {
    return values.slice(0, 10).map((value) => ({
      name: displayAnonymizedText(value.name),
      count: value.count,
    }));
  }

  function getPaxbotContext(row = paxbotSelectedRow) {
    return {
      page: "Number Range Assistant",
      projectId: idproject,
      numberRange: {
        prefix: row?.prefix,
        length: row?.length,
        occurrences: row?.count,
        examples: row?.sample_values,
        topApps: (row?.sample_clearnames || []).slice(0, 10),
        tasks: getPaxbotTopValues(row?.tasks || []),
        activities: getPaxbotTopValues(row?.activities || []),
        connections: (row?.connections || []).slice(0, 10),
        masks: getPaxbotTopValues(row?.windowtitles || []),
        uiElements: getPaxbotTopValues(row?.locations || []),
      },
    };
  }

  async function requestPaxbotAnswer(question, history, row) {
    paxbotLoading = true;

    try {
      const response = await fetchWithSession(`/api/damprojects/paxbot/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question,
          history,
          context: getPaxbotContext(row),
        }),
      });
      const responseText = await response.text();
      let result = {};
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        throw new Error(
          `Paxbot endpoint returned ${response.status || "an invalid response"}`,
        );
      }
      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "Pax AI request failed");
      }
      paxbotLoading = false;
      animatePaxbotAnswer(result.answer);
    } catch (error) {
      clearPaxbotTyping();
      paxbotChatMessages = [
        ...paxbotChatMessages,
        {
          role: "assistant",
          text: `Pax AI could not retrieve an answer right now: ${error.message}`,
        },
      ];
    } finally {
      paxbotLoading = false;
    }
  }

  async function openPaxbotForSuggestion(row) {
    if (paxbotLoading || paxbotTyping) return;
    paxbotVisible = true;
    paxbotSelectedRow = row;
    paxbotInput = "";
    clearPaxbotTyping();
    const question =
      "Was könnte diese Number Range fachlich sein, wie sicher wirkt das, und lohnt sie sich als Tracking-Objekt?";
    paxbotChatMessages = [{ role: "user", text: question }];
    await requestPaxbotAnswer(question, [], row);
  }

  async function handlePaxbotSubmit() {
    const question = paxbotInput.trim();
    if (!question || paxbotLoading || paxbotTyping || !paxbotSelectedRow)
      return;

    const history = paxbotChatMessages;
    paxbotChatMessages = [
      ...paxbotChatMessages,
      { role: "user", text: question },
    ];
    paxbotInput = "";
    await requestPaxbotAnswer(question, history, paxbotSelectedRow);
  }

  function handlePaxbotKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handlePaxbotSubmit();
    }
  }

  function closePaxbot() {
    paxbotVisible = false;
  }

  function hasPreviewConnections(row) {
    const suggestion =
      Array.isArray(row?.connections) && row.connections.length
        ? row
        : findSuggestionForRule(row);
    return (
      Array.isArray(suggestion?.connections) &&
      suggestion.connections.length > 0
    );
  }

  async function openNumberRangePreview(row, requireConnections = true) {
    if (requireConnections && !hasPreviewConnections(row)) return;
    const requestId = ++previewRequestId;
    previewRow = row;
    previewGraph = { nodes: [], edges: [] };
    previewError = "";
    previewLoading = true;
    numberRangePreviewOpen = true;

    const params = new URLSearchParams({
      caseids: JSON.stringify([{ prefix: row.prefix, length: row.length }]),
      startnodes: JSON.stringify([]),
    });
    try {
      const response = await fetchWithSession(
        `/api/damprojects/case-id-graphs/${idproject}?${params.toString()}`,
        { method: "GET" },
      );
      if (!response.ok) throw new Error("Preview could not be loaded.");
      const data = await response.json();
      if (requestId === previewRequestId) {
        previewGraph = data || { nodes: [], edges: [] };
      }
    } catch (error) {
      if (requestId === previewRequestId) previewError = error.message;
    } finally {
      if (requestId === previewRequestId) previewLoading = false;
    }
  }

  function openEditDataModal(id) {
    if (id) {
      currentRule = rows.find((x) => x.id == id);
    } else {
      currentRule = {
        record_type: "",
        prefix: "",
        length: "",
        padding: false,
        track: true,
      };
    }
    prefillNumbersToCheck = "";
    prefillRequestId += 1;
    addRuleModalOpen = true;
  }

  function confirmDelete(id) {
    idToDelete = id;
    openDangerModal = true;
  }

  function handleSaved(event) {
    const { data, isNew } = event.detail;
    if (isNew) {
      rows = [...rows, data];
    } else {
      rows = rows.map((row) => (row.id === data.id ? data : row));
    }
  }

  function handleDeleted(event) {
    const { id } = event.detail;
    rows = rows.filter((row) => row.id !== id);
  }

  function addSuggestionAsRule(suggestion) {
    if (suggestion.hasExistingRule) return;
    prefillNumbersToCheck = Array.isArray(suggestion.sample_values)
      ? suggestion.sample_values.join("\n")
      : "";
    prefillRequestId += 1;
    currentRule = {
      record_type: "",
      prefix: suggestion.prefix,
      length: suggestion.length,
      padding: false,
      track: true,
    };
    addRuleModalOpen = true;
  }

  const configuredColumnActions = {
    menu: [
      {
        icon: EditIcon,
        label: "Edit",
        onClick: (row) => openEditDataModal(row.id),
      },
      {
        icon: RemoveIcon,
        label: "Delete",
        danger: true,
        onClick: (row) => confirmDelete(row.id),
      },
    ],
  };

  $: suggestionColumnActions = {
    add: [
      {
        icon: EditIcon,
        label: "Add as rule",
        disabled: (row) => row.hasExistingRule,
        onClick: (row) => addSuggestionAsRule(row),
      },
      {
        icon: ChatbotIcon,
        label: "Ask Paxbot",
        disabled: paxbotLoading || paxbotTyping,
        onClick: (row) => openPaxbotForSuggestion(row),
      },
    ],
  };

  $: if (idproject && idproject !== loadedProjectId) {
    loadedProjectId = idproject;
    getData();
    getNumberRangeSuggestions();
  }

  $: pageLoading = configuredRangesLoading || suggestionsLoading;
  $: pageLoadingTitle =
    configuredRangesLoading && suggestionsLoading
      ? "Loading number range data."
      : configuredRangesLoading
        ? "Loading configured number ranges."
        : "Loading suggested number ranges.";
  $: pageLoadingHint =
    configuredRangesLoading && suggestionsLoading
      ? "Please wait while configured number ranges and detected candidates are loaded."
      : configuredRangesLoading
        ? "Please wait while configured number ranges are loaded."
        : "Please wait while detected number range candidates are identified.";

  onDestroy(() => {
    clearPaxbotTyping();
  });

  $$restProps;
</script>

<Header
  section="Define Number Ranges"
  area="Discovery"
  breadcrumb="Define Number Ranges"
  tooltipText="Define number ranges used to identify business objects in registered tasks."
>
  <div data-guide="complete-number-ranges">
    <ProjectStepActions
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      text="Complete Number Range Setup"
      completionWarning="This completes the current number range setup step. You can still change or extend the number ranges later during the analysis if needed."
    />
  </div>
</Header>
<h1 class="visually-hidden">Number Range Assistant</h1>
<SectionWrapper
  fullscreen={true}
  seamless={true}
  seamlessPadding={true}
  loading={pageLoading}
  loadingTitle={pageLoadingTitle}
  loadingHint={pageLoadingHint}
>
  <div class="main" slot="main">
    <div data-guide="number-range-overview">
      <InfoBlock
        type="info"
        heading="Configured Number Ranges"
        message="Manage the number ranges currently used to identify business objects in registered tasks. When you are ready, click &quot;Complete Number Range Setup&quot;. You can still change or extend the number ranges later during the analysis if needed."
        showClose={false}
      />
    </div>
    <div class="suggested-table-wrapper" data-guide="configured-number-ranges">
      <DataTableBlack
        {headers}
        rows={filteredRows}
        sortable
        slicePage
        {searchValue}
        bind:page={configuredPage}
        bind:pageSize={configuredPageSize}
        on:search={(e) => {
          searchValue = e.detail.value;
        }}
        totalItems={filteredRows.length}
        pageSizes={[10, 25, 50]}
        columnActions={configuredColumnActions}
        newButtonIconLeft={false}
        newButtonSetting={true}
        newButtonIcon={null}
        newButtonText="Add Number Range"
        on:addNew={() => {
          openEditDataModal(0);
        }}
      >
        <svelte:fragment slot="cell" let:cell let:row>
          {#if cell.key === "padding" || cell.key === "track"}
            {#if cell.value === true}
              <img
                src={"/img/assistant/icons/check_dark.svg"}
                alt="Check Icon"
                class="icon"
              />
            {:else}
              <img
                src={"/img/assistant/icons/close_dark.svg"}
                alt="Cross Icon"
                class="icon"
              />
            {/if}
          {:else}
            {cell.value || cell.value === 0 ? cell.value : ""}
          {/if}
        </svelte:fragment>
      </DataTableBlack>
    </div>
    <InfoBlock
      type="info"
      heading="Suggested Number Ranges"
      message="Review detected number range candidates and add relevant suggestions as tracking rules."
      showClose={false}
    />
    <div
      class="suggestion-table-wrapper"
      data-guide="suggested-number-ranges"
      class:detail-view={suggestionView === 1}
    >
      {#if suggestionsLoading}
        <div class="suggestions-loading" role="status">
          <Loading small withOverlay={false} />
          <span>Loading detected objects…</span>
        </div>
      {:else}
        <DataTableBlack
          headers={suggestionHeaders}
          rows={filteredSuggestionRows}
          sortable
          slicePage
          showNewButton={false}
          searchValue={suggestionSearchValue}
          bind:sortKey={suggestionSortKey}
          bind:sortDirection={suggestionSortDirection}
          bind:page={suggestionPage}
          bind:pageSize={suggestionPageSize}
          on:search={(e) => {
            suggestionSearchValue = e.detail.value;
          }}
          totalItems={filteredSuggestionRows.length}
          pageSizes={[10, 25, 50, 100]}
          columnActions={suggestionColumnActions}
        >
          <div slot="left-toolbar" class="suggestion-table-controls">
            <ContentSwitcher
              options={["Overview", "Details"]}
              bind:selected={suggestionView}
              fitContent
            />
            <label class="connection-filter">
              <span>Min. connections</span>
              <input
                type="number"
                min="0"
                step="1"
                value={minConnections}
                on:input={(event) => {
                  minConnections = Math.max(
                    0,
                    Number(event.currentTarget.value) || 0,
                  );
                  suggestionPage = 1;
                }}
              />
            </label>
          </div>
          <svelte:fragment slot="cell" let:cell let:row>
            {#if ["prefix", "length", "count"].includes(cell.key)}
              <span class="compact-number-range-cell">{cell.value ?? ""}</span>
            {:else if cell.key === "sample_values_display"}
              <span class="sample-values" title={cell.value || ""}
                >{cell.value || ""}</span
              >
            {:else if cell.key === "locations_display"}
              <span
                class="top-values"
                title={getTopValuesTooltip(row.locations)}
              >
                {cell.value || ""}
              </span>
            {:else if cell.key === "windowtitles_display"}
              <span
                class="top-values"
                title={getTopValuesTooltip(row.windowtitles)}
              >
                {cell.value || ""}
              </span>
            {:else if cell.key === "tasks_display"}
              <span class="top-values" title={getTopValuesTooltip(row.tasks)}>
                {cell.value || ""}
              </span>
            {:else if cell.key === "activities_display"}
              <span
                class="top-values"
                title={getTopValuesTooltip(row.activities)}
              >
                {cell.value || ""}
              </span>
            {:else if cell.key === "sample_clearnames_display"}
              <span class="sample-apps" title={cell.value || ""}
                >{cell.value || ""}</span
              >
            {:else}
              <span class:existing-rule={row.hasExistingRule}>
                {cell.value || cell.value === 0 ? cell.value : ""}
              </span>
            {/if}
          </svelte:fragment>
        </DataTableBlack>
      {/if}
    </div>
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showRangeGuide}
  bind:currentStep={rangeGuideStep}
  steps={rangeGuideSteps}
  allowInteraction={false}
/>

{#if paxbotVisible}
  <section class="paxbot_chat_window" aria-label="Pax AI chat">
    <header class="paxbot_chat_header">
      <div>
        <strong>Pax AI</strong>
        <span>Number Range Assistant</span>
      </div>
      <button
        type="button"
        class="paxbot_close_button"
        aria-label="Close Pax AI"
        on:click={closePaxbot}
      >
        <img src="/img/assistant/icons/close.svg" alt="" />
      </button>
    </header>
    <div class="paxbot_chat_messages" bind:this={paxbotMessagesElement}>
      {#if paxbotSelectedRow}
        <div class="paxbot_context_message">
          <strong>Selected number range</strong>
          <p>
            Prefix {paxbotSelectedRow.prefix}, length {paxbotSelectedRow.length},
            count {paxbotSelectedRow.count}
          </p>
        </div>
      {/if}
      {#each paxbotChatMessages as message}
        <div
          class:paxbot_user_message={message.role === "user"}
          class="paxbot_message_row"
        >
          {#if message.role === "assistant"}
            <img src="/img/assistant/icons/robot_2.svg" alt="" />
          {/if}
          <div class="paxbot_message_bubble">
            <span>{message.role === "user" ? "You" : "Pax AI"}</span>
            <p>{message.text}</p>
          </div>
        </div>
      {/each}
      {#if paxbotLoading}
        <div class="paxbot_message_row">
          <img src="/img/assistant/icons/robot_2.svg" alt="" />
          <div class="paxbot_message_bubble">
            <span>Pax AI</span>
            <p>Thinking...</p>
          </div>
        </div>
      {/if}
    </div>
    <div class="paxbot_disclaimer">
      Pax AI considers only the selected number range here.
    </div>
    <form
      class="paxbot_chat_input"
      on:submit|preventDefault={handlePaxbotSubmit}
    >
      <textarea
        bind:value={paxbotInput}
        rows="1"
        placeholder={paxbotLoading
          ? "Pax AI is thinking..."
          : paxbotTyping
            ? "Pax AI is replying..."
            : "Ask about this number range..."}
        aria-label="Ask Pax AI"
        on:keydown={handlePaxbotKeydown}
      ></textarea>
      <button
        type="submit"
        aria-label="Send message"
        disabled={!paxbotInput.trim() || paxbotLoading || paxbotTyping}
      >
        <img src="/img/assistant/icons/arrow_right.svg" alt="" />
      </button>
    </form>
  </section>
  <img
    class="paxbot_chat_mascot"
    src="/img/assistant/paxbot_portrait.png"
    alt="Pax AI"
  />
{/if}

<NumberRuleModal
  bind:open={addRuleModalOpen}
  bind:currentRule
  bind:openDangerModal
  {idToDelete}
  {idproject}
  {prefillNumbersToCheck}
  {prefillRequestId}
  on:saved={handleSaved}
  on:deleted={handleDeleted}
/>

{#if numberRangePreviewOpen}
  <Modal
    bind:open={numberRangePreviewOpen}
    modalHeading="Object Explorer Preview"
    size="lg"
    width={1100}
    hideFooter={true}
  >
    <div class="preview-summary">
      <strong>{previewRow?.record_type || "Suggested number range"}</strong>
      <span
        >Prefix {previewRow?.prefix || "–"}, length {previewRow?.length ||
          "–"}</span
      >
    </div>
    <div class="preview-canvas">
      {#if previewLoading}
        <Loading withOverlay={false} />
      {:else if previewError}
        <p class="preview-message error">{previewError}</p>
      {:else if !(previewGraph.nodes || []).length}
        <p class="preview-message">
          No multi-task cases were found for this number range.
        </p>
      {:else}
        <CaseIdGraphPreview
          nodesData={previewGraph.nodes || []}
          edgesData={previewGraph.edges || []}
        />
      {/if}
    </div>
  </Modal>
{/if}

<style>
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    border: 0;
  }
  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .suggested-table-wrapper {
    margin-bottom: 0;
  }

  .main :global(.custom-table .bx--toolbar-content) {
    gap: var(--control-gap);
    margin-bottom: var(--control-gap);
  }

  .main :global(.custom-table .cell-center) {
    gap: var(--control-gap);
  }

  .suggestions-loading {
    min-height: 8rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
  }

  .suggestion-table-wrapper :global(th[data-header="prefix"]),
  .suggestion-table-wrapper :global(th[data-header="length"]),
  .suggestion-table-wrapper :global(th[data-header="count"]) {
    width: max-content;
    min-width: max-content;
    white-space: nowrap;
  }

  .compact-number-range-cell {
    display: inline-block;
    white-space: nowrap;
  }

  .suggestion-table-controls {
    align-self: flex-start;
    display: flex;
    align-items: flex-start;
    gap: var(--control-gap);
  }

  .connection-filter {
    display: flex;
    align-items: center;
    gap: var(--control-gap);
    height: 3rem;
    color: var(--pax-black);
    font-size: 0.75rem;
    white-space: nowrap;
  }

  .connection-filter input {
    width: 4.5rem;
    height: 2rem;
    padding: 0 0.5rem;
    border: 1px solid var(--pax-black);
    border-radius: 0.25rem;
    background: var(--pax-white);
    color: var(--pax-black);
  }

  .suggestion-table-controls :global(.grid_container) {
    align-self: flex-start;
    height: 3rem !important;
    min-height: 3rem;
    max-height: 3rem;
  }

  .sample-values {
    color: var(--cds-text-secondary, #525252);
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
    white-space: nowrap;
  }

  .sample-apps {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
    white-space: nowrap;
  }

  .preview-message {
    margin: 0;
    color: #6f7681;
  }

  .preview-message.error {
    color: #da1e28;
  }

  .paxbot_chat_window {
    position: fixed;
    right: 1.5rem;
    bottom: 5.5rem;
    z-index: 9000;
    width: min(28rem, calc(100vw - 2rem));
    height: min(31rem, calc(100vh - 7rem));
    min-height: 18rem;
    display: flex;
    flex-direction: column;
    border-radius: 0.5rem;
    background: #fff;
    border: 1px solid #e1e4e8;
    box-shadow: 0 1rem 2.5rem rgba(24, 28, 34, 0.18);
    overflow: visible;
  }

  .paxbot_chat_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--control-gap);
    padding: 0.9rem 1rem;
    background: var(--pax-orange);
    color: white;
  }

  .paxbot_chat_header div {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }

  .paxbot_chat_header strong {
    font-size: 0.95rem;
    line-height: 1.2;
  }

  .paxbot_chat_header span {
    font-size: 0.75rem;
    opacity: 0.86;
  }

  .paxbot_close_button {
    height: 1.75rem;
    width: 1.75rem;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.35rem;
    background: transparent;
    cursor: pointer;
  }

  .paxbot_close_button:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  .paxbot_close_button img {
    height: 0.75rem;
    width: 0.75rem;
    filter: brightness(0) invert(1);
  }

  .paxbot_chat_messages {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    background: #f7f8fa;
  }

  .paxbot_context_message {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    padding: 0.75rem;
    border-radius: 0.45rem;
    border: 1px solid #e0e4e8;
    background: #fff;
    color: #24282d;
    font-size: 0.78rem;
  }

  .paxbot_context_message strong {
    font-size: 0.78rem;
    line-height: 1.3;
  }

  .paxbot_context_message p,
  .paxbot_message_bubble p {
    margin: 0;
    line-height: 1.4;
    white-space: pre-line;
    overflow-wrap: anywhere;
  }

  .paxbot_message_row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
  }

  .paxbot_message_row img {
    width: 1rem;
    height: 1rem;
    margin-top: 0.35rem;
    flex: 0 0 auto;
  }

  .paxbot_message_row.paxbot_user_message {
    justify-content: flex-end;
  }

  .paxbot_message_bubble {
    max-width: 82%;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.7rem 0.8rem;
    border-radius: 0.5rem;
    background: #fff;
    color: #24282d;
    border: 1px solid #e4e6ea;
    font-size: 0.82rem;
  }

  .paxbot_user_message .paxbot_message_bubble {
    background: var(--pax-black);
    color: #fff;
    border-color: var(--pax-black);
  }

  .paxbot_message_bubble span {
    font-size: 0.72rem;
    font-weight: 700;
    opacity: 0.75;
  }

  .paxbot_disclaimer {
    padding: 0.45rem 0.85rem 0;
    background: #fff;
    color: #8a929b;
    font-size: 0.68rem;
    line-height: 1.25;
    text-align: center;
    border-top: 1px solid #eef0f2;
  }

  .paxbot_chat_input {
    display: grid;
    grid-template-columns: 1fr 2.5rem;
    gap: var(--control-gap);
    padding: 0.55rem 0.75rem 0.75rem;
    background: #fff;
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  .paxbot_chat_input textarea {
    min-height: 2.5rem;
    max-height: 7rem;
    resize: vertical;
    border: 1px solid #d7dbe0;
    border-radius: 0.45rem;
    padding: 0.65rem 0.75rem;
    font: inherit;
    font-size: 0.82rem;
    outline: none;
  }

  .paxbot_chat_input textarea:focus {
    border-color: var(--pax-orange);
  }

  .paxbot_chat_input button {
    height: 2.5rem;
    width: 2.5rem;
    border: 0;
    border-radius: 0.45rem;
    background: var(--pax-black);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .paxbot_chat_input button:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  .paxbot_chat_input button:not(:disabled):hover {
    background: var(--pax-orange);
  }

  .paxbot_chat_input button img {
    width: 1rem;
    height: 1rem;
    filter: brightness(0) invert(1);
  }

  .paxbot_chat_mascot {
    position: fixed;
    right: 2rem;
    bottom: -0.7rem;
    z-index: 9001;
    height: 5.6rem;
    width: auto;
    pointer-events: none;
  }

  @media (max-width: 720px) {
    .paxbot_chat_window {
      right: 0.5rem;
      width: calc(100vw - 1rem);
      height: min(28rem, calc(100vh - 7.25rem));
      bottom: 5rem;
    }

    .paxbot_chat_mascot {
      right: 1rem;
      bottom: -0.5rem;
      height: 4.8rem;
    }
  }
</style>
